import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { useGetRentalLocationOperatingHours } from 'src/hooks/useGetRentalLocationOperatingHours';
import { useGetRental } from 'src/hooks/useGetRental';
import {
    buildISODateTimeWithOffset,
    operationOnMilitaryTime,
    colonTimeToMinutesSinceMidnight,
} from 'src/utils/DateHelper';

import { ArrivalTimeFormState } from '../arrivalTime.types';
import {
    useGetRentalArrivalTime,
    useCreateRentalArrivalTime,
    useUpdateRentalArrivalTime,
} from './arrivalTime.queries';
import { useConfiguration } from 'src/hooks/useConfiguration';

export const useArrivalTimeForm = (successCallback: () => void) => {
    const { isLoading, data: rentalArrivalTime } = useGetRentalArrivalTime();
    const { mutate: create, isPending: isCreating } =
        useCreateRentalArrivalTime();
    const { mutate: update, isPending: isUpdating } =
        useUpdateRentalArrivalTime();
    const { isLoading: isOperatingHoursLoading, operatingHours } =
        useGetRentalLocationOperatingHours();
    const rental = useGetRental();
    const configuration = useConfiguration();

    const validationSchema = yup.object().shape({
        arrivalTime: yup
            .string()
            .required('Arrival time is required')
            .matches(/^\d{2}:\d{2}$/, 'Arrival time is required')
            .test(
                'within-operating-hours',
                'Arrival time must be within operating hours and at least 30 minutes before the branch closes',
                (value) => {
                    if (
                        !value ||
                        !operatingHours?.start?.rawOpen ||
                        !operatingHours?.start?.rawClose
                    ) {
                        return true;
                    }
                    const rawCloseWithBuffer = operationOnMilitaryTime(
                        operatingHours.start.rawClose,
                        'subtract',
                        configuration.functionalities.arrivalTime
                            .closeTimeBuffer,
                    );

                    const selected = colonTimeToMinutesSinceMidnight(value);
                    const open = colonTimeToMinutesSinceMidnight(
                        operatingHours.start.rawOpen,
                    );
                    const close =
                        colonTimeToMinutesSinceMidnight(rawCloseWithBuffer);

                    return selected >= open && selected <= close;
                },
            ),
        flightNumber: yup.string().optional(),
    });

    const isExisting = !!rentalArrivalTime;

    const { control, handleSubmit, formState, reset } =
        useForm<ArrivalTimeFormState>({
            resolver: yupResolver(validationSchema) as any,
            defaultValues: {
                arrivalTime: '',
                flightNumber: '',
            },
            mode: 'onChange',
        });

    useEffect(() => {
        if (rentalArrivalTime) {
            reset({
                arrivalTime: rentalArrivalTime.arrivalTime
                    ? rentalArrivalTime.arrivalTime.slice(11, 16)
                    : '',
                flightNumber: rentalArrivalTime.flightNumber ?? '',
            });
        }
    }, [rentalArrivalTime, reset]);

    const onSubmit = handleSubmit(
        useCallback(
            (formData: ArrivalTimeFormState) => {
                if (!rental?.start?.date) {
                    throw new Error('Rental start date is not available');
                }
                const arrivalTimeISO = buildISODateTimeWithOffset(
                    rental.start.date,
                    formData.arrivalTime,
                );

                if (isExisting) {
                    update(
                        {
                            rentalArrivalTimeId:
                                rentalArrivalTime.id.rentalArrivalTimeId,
                            payload: {
                                id: rentalArrivalTime.id,
                                arrivalTime: arrivalTimeISO,
                                flightNumber: formData.flightNumber,
                            },
                        },
                        { onSuccess: successCallback },
                    );
                } else {
                    create(
                        {
                            arrivalTime: arrivalTimeISO,
                            flightNumber: formData.flightNumber,
                        },
                        { onSuccess: successCallback },
                    );
                }
            },
            [
                isExisting,
                rentalArrivalTime,
                rental,
                create,
                update,
                successCallback,
            ],
        ),
    );

    return {
        control,
        formState,
        isLoading,
        isPending: isCreating || isUpdating || isOperatingHoursLoading,
        operatingHours,
        pickUpBranchName: rental?.start?.point?.name,
        pickUpDate: rental?.start?.date,
        onSubmit,
    };
};
